import { Navigate } from "react-router-dom";
import { useQuery, useQueryClient, useMutation } from "react-query";
import axios from "axios";
import { Sheet, Stack, Typography } from "@mui/joy";
import Dropdown from "@mui/joy/Dropdown";
import MenuButton from "@mui/joy/MenuButton";
import Menu from "@mui/joy/Menu";
import MenuItem from "@mui/joy/MenuItem";

import { useIsAdmin } from "../../hooks/useIsAdmin";
import { PrimeQueryKeys } from "../../shared/constants";
import { FileApi } from "../../api/file";
import { AdminApi } from "../../api/admin";

export default function AdminKgManagement() {
    const isAdmin = useIsAdmin();
    const queryClient = useQueryClient();

    // Query to fetch your KG info
    const kgQuery = useQuery<{ file_id: string; document_type: string }[]>(
        PrimeQueryKeys.adminKgFiles,
        AdminApi.getKgFiles,
    );

    // Query to fetch all known files
    const { data: fileData } = useQuery(PrimeQueryKeys.allFiles, FileApi.getAllFiles);
    const files = fileData ?? [];

    // A helper for checking if a file ID actually exists in `files`
    const getFile = (id: string) => files.find((f) => f.id === id);

    // 1. Define the mutation to POST to /admin/kgFileRemap
    const fileRemapMutation = useMutation<any, any, { fromFileId: string; toFileId: string }>(
        async ({ fromFileId, toFileId }) => {
            const resp = await axios.post("/api/admin/kgFileRemap", {
                fromFileId,
                toFileId,
            });
            return resp.data;
        },
        {
            onSuccess: async () => {
                await queryClient.invalidateQueries(PrimeQueryKeys.adminKgFiles);
            },
        },
    );

    if (!isAdmin) {
        return <Navigate to="/" />;
    }

    return (
        <Stack spacing={2} margin={2}>
            <Typography>
                This lists all the nodes with the <code>HasFileID</code> label. If change a file id
                it will remap
                <em>ALL</em> the nodes with that file id, not just that single node displayed. You
                may need to refresh this page to see the changes reflected.
            </Typography>
            {(kgQuery.data ?? []).map((f) => {
                const file = getFile(f.file_id);
                const fileExists = Boolean(file);
                return (
                    <Sheet variant="soft" color={fileExists ? "neutral" : "danger"} key={f.file_id}>
                        <Stack padding={2} spacing={1}>
                            <Stack direction="row" spacing={2} alignItems="center">
                                <Typography level="title-md">KG File ID: {f.file_id}</Typography>
                                {fileExists ? (
                                    <Typography color="success">{` ✅ File mapped to ${
                                        file?.fileName
                                    }`}</Typography>
                                ) : (
                                    <Typography color="danger">❌ File does not exist</Typography>
                                )}
                                <Dropdown>
                                    <MenuButton variant="plain" size="sm">
                                        Remap
                                    </MenuButton>
                                    <Menu>
                                        {files.map((dbf) => (
                                            <MenuItem
                                                key={dbf.id}
                                                onClick={() => {
                                                    fileRemapMutation.mutate({
                                                        fromFileId: f.file_id,
                                                        toFileId: dbf.id,
                                                    });
                                                }}
                                            >
                                                {dbf.fileName}
                                            </MenuItem>
                                        ))}
                                    </Menu>
                                </Dropdown>
                            </Stack>
                            <Stack direction="row" spacing={1}>
                                <Typography level="body-sm" fontWeight="bold">
                                    DocumentType:
                                </Typography>
                                <Typography level="body-sm" fontFamily="monospace">
                                    {f.document_type}
                                </Typography>
                            </Stack>
                        </Stack>
                    </Sheet>
                );
            })}
        </Stack>
    );
}
